import * as signalR from '@microsoft/signalr';
import { getCallStatus } from './callMonitoring.service';
import { DEFAULT_API_CONFIG } from './apiConfig';

class CallMonitor {
  constructor() {
    this.connection = null;
    this.callSubscriptions = new Set();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.updateHandlers = new Set();
  }

  async start() {
    try {
      // Get base URL by removing '/api/v1' from the API URL
      const baseUrl = DEFAULT_API_CONFIG.url.replace('/api/v1', '');
      const hubUrl = `${baseUrl}/hubs/call`;
      
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Include auth headers and other settings
          headers: DEFAULT_API_CONFIG.getHeaders(),
          withCredentials: false,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.setupEventHandlers();
      await this.connection.start();
      this.isConnected = true;
      await this.connection.invoke('JoinGroup', 'ActiveCalls');
      console.log('SignalR Connected to:', hubUrl);
    } catch (error) {
      console.error('Error starting SignalR connection:', error);
      throw error;
    }
  }

  setupEventHandlers() {
    this.connection.on('CallInitiated', this.handleCallInitiated);
    this.connection.on('CallUpdate', this.handleCallUpdate);
    this.connection.on('ReceiveCallUpdate', this.handleCallUpdate);

    this.connection.onclose(this.handleConnectionClose);
    this.connection.onreconnecting(this.handleReconnecting);
    this.connection.onreconnected(this.handleReconnected);
  }

  onUpdate(handler) {
    this.updateHandlers.add(handler);
    return () => this.updateHandlers.delete(handler);
  }

  handleCallInitiated = async (update) => {
    console.log('New call initiated:', update);
    // Fetch initial call status
    try {
      const status = await getCallStatus(update.callId);
      this.updateHandlers.forEach(handler => handler('initiated', { ...update, ...status }));
    } catch (error) {
      console.error('Error fetching initial call status:', error);
      this.updateHandlers.forEach(handler => handler('initiated', update));
    }
  };

  handleCallUpdate = (update) => {
    // Only log if the update contains certain fields of interest
    if (update.status || update.duration || update.transcription) {
      console.log('Call update received:', update);
    }
    this.updateHandlers.forEach(handler => handler('update', update));
  };

  handleConnectionClose = (error) => {
    this.isConnected = false;
    console.error('SignalR connection closed:', error);
  };

  handleReconnecting = (error) => {
    console.log('Attempting to reconnect to SignalR...', error);
  };

  handleReconnected = async (connectionId) => {
    this.isConnected = true;
    console.log('SignalR reconnected with ID:', connectionId);
    
    // Rejoin active calls group
    await this.connection.invoke('JoinGroup', 'ActiveCalls');
    
    // Resubscribe to all active calls and fetch latest status
    for (const callId of this.callSubscriptions) {
      try {
        await this.subscribeToCall(callId);
        const status = await getCallStatus(callId);
        this.updateHandlers.forEach(handler => handler('update', status));
      } catch (error) {
        console.error(`Error resubscribing to call ${callId}:`, error);
      }
    }
  };

  async subscribeToCall(callId) {
    if (!this.isConnected) {
      throw new Error('SignalR connection not established');
    }

    try {
      await this.connection.invoke('SubscribeToCall', callId);
      this.callSubscriptions.add(callId);
      console.log(`Subscribed to call: ${callId}`);
    } catch (error) {
      console.error(`Error subscribing to call ${callId}:`, error);
      throw error;
    }
  }

  async unsubscribeFromCall(callId) {
    if (!callId) {
      console.warn('Attempted to unsubscribe from call with no callId');
      return;
    }
    
    if (!this.isConnected) {
      console.warn(`Cannot unsubscribe from call ${callId}: SignalR connection not established`);
      // Still remove from local tracking even if we can't tell the server
      this.callSubscriptions.delete(callId);
      return;
    }

    try {
      await this.connection.invoke('UnsubscribeFromCall', callId);
      this.callSubscriptions.delete(callId);
      console.log(`Unsubscribed from call: ${callId}`);
    } catch (error) {
      console.error(`Error unsubscribing from call ${callId}:`, error);
      // Still remove from local tracking even if server call failed
      this.callSubscriptions.delete(callId);
    }
  }

  async stop() {
    if (this.connection) {
      await this.connection.stop();
      this.isConnected = false;
      this.callSubscriptions.clear();
      this.updateHandlers.clear();
      console.log('SignalR connection stopped');
    }
  }
}

export const callMonitor = new CallMonitor(); 